/**
 * Pure display helpers for the character-details sections.
 */

/**
 * Classes whose spells are fixed rather than prepared each day. Mirrors
 * `is_known_caster_class` in backend/shared/src/rest/adventure_prep/mod.rs.
 */
const KNOWN_CASTER_CLASSES = new Set(['bard', 'sorcerer', 'warlock']);

/**
 * The character's real daily preparation cap, or undefined when they have none.
 *
 * `max_prepared_spells` is not a cap for every caster. The backend fills it for
 * known casters too, where it means "total spells you can know" (see the doc
 * comment on `max_prepared_spells`). Passing that through as a cap made a
 * Warlock read `0/4` next to its patron spells, since a known caster never
 * fills `prepared_spells` and so has a chosen count of zero.
 */
export function preparationCap(
  className: string | undefined,
  maxPreparedSpells: number | undefined
): number | undefined {
  if (className && KNOWN_CASTER_CLASSES.has(className.toLowerCase())) return undefined;
  return maxPreparedSpells;
}

/**
 * Text for the Prepared Spells badge.
 *
 * With a prep cap, always-prepared spells (feat / domain / subclass grants) do
 * not count against it per SRD 5.2.1, so the badge reads chosen/max. Without a
 * cap (a Monk or Fighter whose only spells come from Magic Initiate, or a known
 * caster) the chosen-against-cap count is meaningless: it rendered a bare `0`
 * next to a list of spells the player could plainly see. Show the listed count.
 */
export function preparedBadgeText(
  listedCount: number,
  chosenPreparedCount?: number,
  maxPreparedSpells?: number
): string {
  if (!maxPreparedSpells) return String(listedCount);
  return `${chosenPreparedCount ?? listedCount}/${maxPreparedSpells}`;
}
